import type { WidgetConfiguration } from "@eu.mdotm.api/sphere";
import { defaultWidgetsGridConfig } from "./default-grids-config";
import type { StoredWidgetEntry, WidgetEntry } from "./types";

/**
 * Converts old coordinate-based API response to the new StoredWidgetEntry[] format.
 * Called once on the data returned by pageWidgetsConfigurationsControllerGetPageConfiguration
 * when no local layout has been stored yet for the grid.
 *
 */
export async function adaptLegacyConfiguration(
	oldConfig: WidgetConfiguration[],
	page: string,
): Promise<StoredWidgetEntry[]> {
	// The widgetMap import is dynamic to break a circular initialization cycle:
	// userStorage → legacy-adapter → WidgetsMapper → (all widgets) → default-configuration → userStorage
	const { widgetMap } = await import("../WidgetsMapper");
	const allowedWidgets = Object.keys(widgetMap);

	const defaultSpanByWidget = Object.fromEntries(
		((defaultWidgetsGridConfig as Record<string, { widgets: WidgetEntry[] }>)[page]?.widgets ?? []).map((w) => [
			w.i,
			w.span,
		]),
	);

	return (
		oldConfig
			.filter((entry) => entry?.i != null && allowedWidgets.includes(entry.i))
			// Sort by y first, then x, to reconstruct reading order
			.sort((a, b) => a.y! - b.y! || a.x! - b.x!)
			.map(({ i }) => ({
				i: i as StoredWidgetEntry["i"],
				span: defaultSpanByWidget[i!] ?? 3,
			}))
	);
}
